// frontend/src/lib/theme.ts
// Light/dark theme controller for the dual-look redesign. Sets
// `data-theme` on <html>; persisted to localStorage. Default light
// (The Writ leads with laid paper). Components subscribe via useTheme().
// prefers-color-scheme is not consulted, on purpose: the theme is the
// player's choice from the header toggle, not the OS's (REDESIGN_SPEC.md).
import { useSyncExternalStore } from 'react';

export type Theme = 'light' | 'dark';
const KEY = 'scotus_theme';

function read(): Theme {
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'light' || v === 'dark') return v;
  } catch { /* ignore */ }
  return 'light';
}

let current: Theme = read();
const listeners = new Set<() => void>();

function apply(t: Theme): void {
  document.documentElement.setAttribute('data-theme', t);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', t === 'dark' ? '#10120E' : '#F0E9D7');
}

/** Apply the persisted theme to <html> as early as possible. */
export function initTheme(): void {
  current = read();
  apply(current);
}

export function getTheme(): Theme { return current; }

export function setTheme(t: Theme): void {
  current = t;
  try { localStorage.setItem(KEY, t); } catch { /* ignore */ }
  apply(t);
  listeners.forEach((l) => l());
}

export function toggleTheme(): void {
  setTheme(current === 'light' ? 'dark' : 'light');
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Reactive hook, re-renders on theme change. Returns the active theme. */
export function useTheme(): Theme {
  return useSyncExternalStore(subscribe, getTheme, () => 'light');
}
